import {
	ConfigPageResponse,
	Field,
	JsonApi,
	Links,
	StateViewResponse
} from '../types/api';

const defaultLink: Links = {
	links: { self: { href: '' } }
};

const defaultJsonApi: JsonApi = {
	version: 'fallback',
	meta: {
		...defaultLink
	}
};

class FieldClass {
	public field: Field;

	constructor({
		value = 'html',
		format = 'default',
		processed = ''
	}: Partial<Field>) {
		this.field = { value, format, processed };
	}
}

export const configFallback: ConfigPageResponse = {
	...defaultLink,
	...defaultJsonApi,
	data: [
		{
			...defaultLink,
			type: 'config',
			id: 'config',
			attributes: {
				field_about: new FieldClass({
					processed:
						'<p>Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet.</p><p> Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua.</p>'
				}).field,
				field_acknowledgements: new FieldClass({
					processed:
						'<p>CAWP would like to thank Pivotal Ventures, an investment and incubation company founded by Melinda French Gates, for their support of this report.</p><p>This report is the result of the hard work of the CAWP and OpenSecrets teams. It would not have been possible without the invaluable assistance of CAWP Research Associate Shikshya Adhikari. Many thanks are also owed to the talented team at Graphicacy for data visualization design and engineering.</p>'
				}).field,
				field_donor_gaps_description: new FieldClass({
					processed:
						'Lorem ipsum dolor sit amet, consumal sadipscing elitr, sed diam nonumy ejan eirmod tempor invidunt ut labore. Lorem ipsum dolor'
				}).field,
				field_national_view_description: new FieldClass({
					processed:
						'Lorem ipsum dolor sit amet, consumal sadipscing elitr, sed diam nonumy ejan eirmod tempor invidunt ut labore.'
				}).field,
				field_state_view_description: new FieldClass({
					processed:
						'Lorem ipsum dolor sit amet, consumal sadipscing elitr, sed diam nonumy ejan eirmod tempor invidunt ut labore.'
				}).field
			}
		}
	]
};

export const stateViewFallback: StateViewResponse = {
	...defaultLink,
	...defaultJsonApi,
	data: [
		{
			...defaultLink,
			type: 'state_view',
			id: 'state_view',
			title: 'Arizona',
			field_state_reference: {
				type: 'state',
				id: 'state',
				links: defaultLink,
				name: 'Arizona',
				field_abbreviation: 'az',
				meta: {
					drupal_internal__target_id: 1
				}
			},
			field_annotation_primary: new FieldClass({
				processed:
					'<p>Fundraising totals for potential state legislative candidates in Ohio show similarity in what women and men are raising for similar races.</p><p>For example, Democratic women state house incumbents and Democratic men state house incumbent candidates are raising similar amounts.</p><p>Some gender differences are apparent in Republican open-seat state senate races with men potential candidates out-raising women potential candidates.</p>'
			}).field,
			field_annotation_secondary: new FieldClass({
				processed:
					'Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores.'
			}).field,
			field_highlights: new FieldClass({
				processed:
					'<ul><li><strong>Democratic women incumbents</strong> are outraising Republican women incumbents in Ohio state legislative races.</li><li>• Men in both parties are more likely to be self-financing their campaigns than women in open-seat races for the Ohio State House.</li><li>Lorem ipsum dolor sit amet, consumal sadipscing elitr, sed diam nonumy ejan eirmod tempor invidunt ut labore. Lorem ipsum dolor</li><li>Lorem ipsum dolor sit amet, consumal sadipscing elitr, sed diam nonumy ejan eirmod tempor invidunt ut labore. Lorem ipsum dolor</li><li>Lorem ipsum dolor sit amet, consumal sadipscing elitr, sed diam nonumy ejan eirmod tempor invidunt ut labore. Lorem ipsum dolor</li></ul>'
			}).field,
			field_intro_text: new FieldClass({
				processed:
					'This page offers a periodic analysis of individual contributions to major party state and congressional candidates in 10 states. These states, most of which are battleground states, represent different regions and partisan dynamics. Because this page only includes data for potential candidates and candidates who have filed campaign finance reports, these statistics may not reflect the total number of candidates who are running. For the number of women candidates, please visit our Election Watch page.'
			}).field,
			field_last_updated: new FieldClass({ processed: 'March 2023' }).field,
			field_key_stats: [
				{
					...defaultLink,
					type: 'key_stat',
					id: 'key_stat',
					meta: {
						drupal_internal__target_id: 1,
						target_revision_id: 1
					},
					field_ks_chart_heading: new FieldClass({
						processed: 'Average Total Raised By Candidate Subgroup'
					}).field,
					field_ks_chart_description: new FieldClass({
						processed:
							'This statistic is the average total amount raised to date from individuals (including self-financing). Primary challengers are excluded.'
					}).field,
					field_ks_text: new FieldClass({
						processed:
							'Fundraising totals for potential state legislative candidates in Ohio show similarity in what women and men are raising for similar races. For example, Democratic women state house incumbents and Democratic men state house incumbent candidates are raising similar amounts. Some gender differences are apparent in Republican open-seat state senate races with men potential candidates out-raising women potential candidates.'
					}).field
				},
				{
					...defaultLink,
					type: 'key_stat',
					id: 'key_stat',
					meta: {
						drupal_internal__target_id: 1,
						target_revision_id: 1
					},
					field_ks_chart_heading: new FieldClass({
						processed: 'Average - % Self-Financed'
					}).field,
					field_ks_chart_description: new FieldClass({
						processed:
							'This statistic is the average amount from self-financing as a proportion of total raised (from individuals including the candidate) by candidate subgroup. Primary challengers are excluded.'
					}).field,
					field_ks_text: new FieldClass({
						processed:
							'Fundraising totals for potential state legislative candidates in Ohio show similarity in what women and men are raising for similar races. For example, Democratic women state house incumbents and Democratic men state house incumbent candidates are raising similar amounts. Some gender differences are apparent in Republican open-seat state senate races with men potential candidates out-raising women potential candidates.'
					}).field
				},
				{
					...defaultLink,
					type: 'key_stat',
					id: 'key_stat',
					meta: {
						drupal_internal__target_id: 1,
						target_revision_id: 1
					},
					field_ks_chart_heading: new FieldClass({
						processed: 'Average - % Contributions $200 or less'
					}).field,
					field_ks_chart_description: new FieldClass({
						processed:
							'This statistic is the average amount of contributions $200 or less as a proportion of total raised (excluding self-financed contributions) by candidate subgroup. Primary challengers are excluded.'
					}).field,
					field_ks_text: new FieldClass({
						processed:
							'Fundraising totals for potential state legislative candidates in Ohio show similarity in what women and men are raising for similar races. For example, Democratic women state house incumbents and Democratic men state house incumbent candidates are raising similar amounts. Some gender differences are apparent in Republican open-seat state senate races with men potential candidates out-raising women potential candidates.'
					}).field
				}
			],
			field_data_appendix: new FieldClass({
				processed:
					'Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est.'
			}).field,
			field_methodology: new FieldClass({
				processed:
					'Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam.'
			}).field
		}
	]
};

export const nationalViewFallback: StateViewResponse = {
	...defaultLink,
	...defaultJsonApi,
	data: [
		{
			...defaultLink,
			type: 'state_view',
			id: 'state_view',
			title: 'Arizona',
			field_state_reference: {
				type: 'state',
				id: 'state',
				links: defaultLink,
				name: 'Arizona',
				field_abbreviation: 'az',
				meta: {
					drupal_internal__target_id: 1
				}
			},
			field_annotation_primary: new FieldClass({
				processed:
					'<p>Fundraising totals for potential state legislative candidates in Ohio show similarity in what women and men are raising for similar races.</p><p>For example, Democratic women state house incumbents and Democratic men state house incumbent candidates are raising similar amounts.</p><p>Some gender differences are apparent in Republican open-seat state senate races with men potential candidates out-raising women potential candidates.</p>'
			}).field,
			field_annotation_secondary: new FieldClass({
				processed:
					'Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores.'
			}).field,
			field_highlights: new FieldClass({
				processed:
					'<ul><li><strong>Democratic women incumbents</strong> are outraising Republican women incumbents in Ohio state legislative races.</li><li>• Men in both parties are more likely to be self-financing their campaigns than women in open-seat races for the Ohio State House.</li></ul>'
			}).field,
			field_intro_text: new FieldClass({
				processed:
					'This page offers a periodic analysis of individual contributions to major party state and congressional candidates in 10 states. These states, most of which are battleground states, represent different regions and partisan dynamics. Because this page only includes data for potential candidates and candidates who have filed campaign finance reports, these statistics may not reflect the total number of candidates who are running. For the number of women candidates, please visit our Election Watch page.'
			}).field,
			field_last_updated: new FieldClass({ processed: 'March 2023' }).field,
			field_key_stats: [
				{
					...defaultLink,
					type: 'key_stat',
					id: 'key_stat',
					meta: {
						drupal_internal__target_id: 1,
						target_revision_id: 1
					},
					field_ks_chart_heading: new FieldClass({
						processed: 'Average Total Raised By Candidate Subgroup'
					}).field,
					field_ks_chart_description: new FieldClass({
						processed:
							'This statistic is the average total amount raised to date from individuals (including self-financing). Primary challengers are excluded.'
					}).field,
					field_ks_text: new FieldClass({
						processed:
							'Fundraising totals for potential state legislative candidates in Ohio show similarity in what women and men are raising for similar races. For example, Democratic women state house incumbents and Democratic men state house incumbent candidates are raising similar amounts. Some gender differences are apparent in Republican open-seat state senate races with men potential candidates out-raising women potential candidates.'
					}).field
				},
				{
					...defaultLink,
					type: 'key_stat',
					id: 'key_stat',
					meta: {
						drupal_internal__target_id: 1,
						target_revision_id: 1
					},
					field_ks_chart_heading: new FieldClass({
						processed: 'Average - % Self-Financed'
					}).field,
					field_ks_chart_description: new FieldClass({
						processed:
							'This statistic is the average amount from self-financing as a proportion of total raised (from individuals including the candidate) by candidate subgroup. Primary challengers are excluded.'
					}).field,
					field_ks_text: new FieldClass({
						processed:
							'Fundraising totals for potential state legislative candidates in Ohio show similarity in what women and men are raising for similar races. For example, Democratic women state house incumbents and Democratic men state house incumbent candidates are raising similar amounts. Some gender differences are apparent in Republican open-seat state senate races with men potential candidates out-raising women potential candidates.'
					}).field
				},
				{
					...defaultLink,
					type: 'key_stat',
					id: 'key_stat',
					meta: {
						drupal_internal__target_id: 1,
						target_revision_id: 1
					},
					field_ks_chart_heading: new FieldClass({
						processed: 'Average - % Contributions $200 or less'
					}).field,
					field_ks_chart_description: new FieldClass({
						processed:
							'This statistic is the average amount of contributions $200 or less as a proportion of total raised (excluding self-financed contributions) by candidate subgroup. Primary challengers are excluded.'
					}).field,
					field_ks_text: new FieldClass({
						processed:
							'Fundraising totals for potential state legislative candidates in Ohio show similarity in what women and men are raising for similar races. For example, Democratic women state house incumbents and Democratic men state house incumbent candidates are raising similar amounts. Some gender differences are apparent in Republican open-seat state senate races with men potential candidates out-raising women potential candidates.'
					}).field
				}
			],
			field_data_appendix: new FieldClass({
				processed:
					'Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est.'
			}).field,
			field_methodology: new FieldClass({
				processed:
					'Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam.'
			}).field
		}
	]
};
